import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { confirmDialog } from 'primereact/confirmdialog'; 
import Fuse from 'fuse.js';

import { AutoCompleteField, Button, TextAreaField } from '../../../helpers';

import { NotificationSchema, notificationInitialState } from '../../../validation/addNotification';
import { insertNotification } from '../../../services/api/adminNotificationMessage';
import { raiseToast } from '../../../services/redux/reducers/toast/toast';

import { capitalizeWordsInValue, getFullName, validateInputs } from '../../../utils';
import { RadioButton } from 'primereact/radiobutton';

const escalationTypeOptions = [
    { label: 'Client', value: 'Client' },
    { label: 'Management', value: 'Management' },
    { label: 'External Vendor', value: 'External Vendor' }
]


const RaiseIssueModal = (props) => {
    const {setIsLoading, notifications, setNotifications, setIsRaiseIssueModalVisible} = props

    const [notification, setNotification] = useState(notificationInitialState)
    const [formErrors, setFormErrors] = useState([])
    const [kamSuggestions, setKAMSuggestions] = useState([])

    const dispatch = useDispatch()

    const companyUsers = useSelector((state) => state.userDetails.PMCompanyDetails.companyUsers).filter(eachUser => eachUser.statusId === 1)
    const priorities = useSelector((state) => state.common.priorities)

    const pmCompanyUsers = companyUsers.map(eachPerson => {
        const name = getFullName(eachPerson.firstName,eachPerson.lastName)
        return {...eachPerson, displayName: name}
    })

    const handleNotificationChange = (event) => {
        notification[event.target.name] = event.target.value

        setNotification({ ...notification })
    }

    const handleSearchOnChange = (event) => {
        let eventValue = event.target.value

        if(eventValue){
            if(eventValue.userId || eventValue.displayName){
                setNotification(prefNotification => ({...prefNotification, userId: eventValue.userId, displayName: eventValue.displayName}))
            } else {
                setNotification(prefNotification => ({...prefNotification, displayName: eventValue}))
            }
        } else {
            setNotification(prefNotification => ({...prefNotification, displayName: null, userId: null}))
        }
    }

    const handleRaiseIssue = async () => {
        setFormErrors({})
        setIsLoading(true)
        
        const notificationRes = await insertNotification({...notification, statusId: 17, notificationTypeId: 2})
        if (notificationRes.failure) {
            setIsLoading(false)
            return dispatch(raiseToast({ severity: 'error', detail: notificationRes.data.error }))
        }

        let updatedNotifications = [notificationRes.data, ...notifications]
        setNotifications(updatedNotifications)

        setNotification(notificationInitialState)
        setIsRaiseIssueModalVisible(false)
        setIsLoading(false)
    }

    const handleConfirmRaiseIssue = async () => {
        const validation_res = await validateInputs(NotificationSchema, notification)

        if (validation_res.error) {
            setFormErrors(validation_res.error)
            return
        } else {
            setFormErrors({})
        }
        confirmDialog({
            message: 'Confirm to raise issue',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => handleRaiseIssue()
        })
    }

    const kamFuzzSearch = useCallback(new Fuse(pmCompanyUsers, {
        keys: ['displayName'],
        includeScore: true,
        threshold: 0.5,
        ignoreLocation: true,
        shouldSort: true, 
        getFn: (obj) => {
            return obj.firstName + obj.lastName
        },
    }), [pmCompanyUsers])

    const kamSearchMethod = (event) => {
        if(event.query === ""){
            setKAMSuggestions([...pmCompanyUsers])
        }
        else{
            const searchResults = kamFuzzSearch.search(event.query)

            const relatedKams = searchResults.map(eachProduct => eachProduct.item)
            setKAMSuggestions(relatedKams)
        }
    }

    return (
        <div className="">
            <div className="grid">
                <div className="flex flex-column col-12 mb-2 lg:col-12 lg:mb-0">
                    <AutoCompleteField
                        dropdown 
                        name="userId" 
                        labelText="User" 
                        value={notification["displayName"]} 
                        field="displayName"
                        dropdownButtonClassName="styled-autocomplete-dropdown-button" 
                        searchMethod={kamSearchMethod} 
                        suggestions={kamSuggestions} 
                        onChange={handleSearchOnChange} 
                        forceSelection 
                        errorMessage={formErrors.userId}
                    />
                </div>

                <div className="flex flex-column col-12 mb-2 lg:col-12 lg:mb-0">
                    <TextAreaField name="message" labelText="Issue" errorMessage={formErrors.message} rows={3} onChange={(e) => handleNotificationChange(e)} value={notification.message} />
                </div>

                <div className="flex flex-column col-12 mb-2 lg:col-12 lg:mb-0">
                    <label htmlFor="" style={{fontSize: '12px', marginBottom: '3px'}}> Escalation Type: { props.required && <span style={{color:"red"}}>*</span> }</label> 
                    <div className="flex flex-row mt-2 pl-3">
                        {escalationTypeOptions.map(eachEscalationType => {
                            return (
                                <div key={`escalationType-${eachEscalationType.label}`} className="flex align-items-center ml-2">
                                    <RadioButton 
                                        className="mr-1"
                                        name="escalationType" 
                                        inputId={`${eachEscalationType.label}`} 
                                        value={eachEscalationType.value} 
                                        onChange={(e) => handleNotificationChange(e)} 
                                        checked={eachEscalationType.value === notification["escalationType"]}
                                    />
                                    <label htmlFor={`${eachEscalationType.label}`} className="mr-2" >{eachEscalationType.label}</label>
                                </div>
                            )
                        })}
                    </div>
                    { formErrors.escalationType && <small className='p-error' style={{fontSize: 'x-small', marginTop: '0.25rem'}}>{formErrors.escalationType}</small> }
                </div>

                <div className="flex flex-column col-12 mb-2 lg:col-12 lg:mb-0">
                    <label htmlFor="" style={{fontSize: '12px', marginBottom: '3px'}}> Priority: { props.required && <span style={{color:"red"}}>*</span> }</label> 
                    <div className="flex flex-row mt-2 pl-3">
                        {priorities.map(eachPriority => {
                            return (
                                <div key={`priority-${eachPriority.priorityId}`} className="flex align-items-center ml-2">
                                    <RadioButton 
                                        className="mr-1"
                                        name="priorityId" 
                                        inputId={`${eachPriority.priorityId}`}
                                        value={eachPriority.priorityId}  
                                        onChange={(e) => handleNotificationChange(e)} 
                                        checked={eachPriority.priorityId === notification["priorityId"]}
                                    />
                                    <label htmlFor={`${eachPriority.priorityId}`} className="mr-2" >{capitalizeWordsInValue(eachPriority.priority)}</label>
                                </div>
                            )
                        })}
                    </div>
                    { formErrors.priorityId && <small className='p-error' style={{fontSize: 'x-small', marginTop: '0.25rem'}}>{formErrors.priorityId}</small> }
                </div>
            </div>
            <div className='mt-3 flex justify-content-center'>
                <Button className="small-btn" label="Raise Issue" onClick={handleConfirmRaiseIssue} />
            </div>
        </div>
    )
}

export default RaiseIssueModal;